import { useMemo } from 'react';

import type { ObjectiveWindow } from '@/types/objectives';

import { useGetObjectives } from '@/api/objectives';
import { summarizeObjectives } from '@/utils/objectivesSummary';

/**
 * The objectives the server returns for a window -- an employee, and a period or a pair of dates --
 * with their headline figures. The profile reads it through its period lens, the manager's
 * evaluation page through the evaluation period's dates; both count the same objectives the same
 * way. `enabled` holds the request back until the window itself is known.
 */
export const useEmployeeObjectives = (objectiveWindow: ObjectiveWindow, enabled = true) => {
    const {
        data: objectives = [],
        isLoading: isLoadingObjectives,
        isError: areObjectivesInError,
        isSuccess: areObjectivesKnown,
    } = useGetObjectives(objectiveWindow, enabled);

    const summary = useMemo(() => summarizeObjectives(objectives), [objectives]);

    return {
        objectives,
        isLoadingObjectives,
        areObjectivesInError,
        // `summarizeObjectives([])` answers zero for everything, and the list is empty before the
        // request lands: a consumer withholds its figures until this is true.
        areObjectivesKnown,
        summary,
    };
};
